import React, {ConsumerProps, useRef} from 'react';
import {AnalyticsBrowser, Context} from '@segment/analytics-next';

import {EventType} from './AnalyticsProvider.types';
import {UserIdentifiedEvent} from './AnalyticsProvider.types';
import {UserSignedOutEvent} from './AnalyticsProvider.types';
import {UserActionEvent} from './AnalyticsProvider.types';
import {PageViewEvent} from './AnalyticsProvider.types';
import {ModalViewEvent} from './AnalyticsProvider.types';

export type SegmentAnalyticTrackPayload = {
  [key: string]: any;
  evtName: EventType;
};

interface SegmentAnalyticContext {
  track(payload: SegmentAnalyticTrackPayload): Promise<Context | void>;
}

const SEGMENT_WRITE_KEY = process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY;

const withSegmentAnalytics = (analytics: AnalyticsBrowser) => {
  const trackUserIdentified = (evt: UserIdentifiedEvent) =>
    analytics.identify(evt.userId, {...(evt.data || {})}).then();

  const trackUserSignedOut = (evt: UserSignedOutEvent) =>
    (analytics.reset() || Promise.resolve()).then();

  const trackUserAction = (evt: UserActionEvent) =>
    analytics.track(evt.actionName, {...(evt.data || {})}).then();

  const trackPageView = (evt: PageViewEvent) =>
    analytics.page(evt.pageName, {...(evt.data || {})}).then();

  const trackModalView = (evt: ModalViewEvent) =>
    analytics.page(evt.modalName, {...(evt.data || {})}).then();

  return {
    trackUserIdentified,
    trackUserSignedOut,
    trackUserAction,
    trackPageView,
    trackModalView,
  };
};

export const SegmentAnalyticsBackend: React.FC<ConsumerProps<SegmentAnalyticContext>> = ({
  children,
}) => {
  // Use an "inner" context value and an "outer" context value where methods in
  // the outer value would simply call the methods in the inner value.
  // The reason for having these 2 sets of value is so that this hook function
  // can return the outer value, and because the outer value is NEVER updated,
  // the consumers of this hook will never see any update and thus would not
  // have rerender triggered.
  const innerContextValue = useRef<SegmentAnalyticContext>({track: () => Promise.resolve()});
  const outerContextValue = useRef<SegmentAnalyticContext>({
    track: payload => innerContextValue.current.track(payload),
  });

  if (SEGMENT_WRITE_KEY) {
    const analytics = AnalyticsBrowser.load({writeKey: SEGMENT_WRITE_KEY});
    const segmentAnalytics = withSegmentAnalytics(analytics);

    // When we do need to update the context value, we only update the "inner"
    // value (which the "outer" value calls). This way we get updated context
    // values without triggering rerender in the consumers.
    innerContextValue.current.track = payload => {
      if (payload.evtName === EventType.UserIdentified) {
        return segmentAnalytics.trackUserIdentified(payload as UserIdentifiedEvent);
      } else if (payload.evtName === EventType.UserSignedOut) {
        return segmentAnalytics.trackUserSignedOut(payload as UserSignedOutEvent);
      } else if (payload.evtName === EventType.UserAction) {
        return segmentAnalytics.trackUserAction(payload as UserActionEvent);
      } else if (payload.evtName === EventType.PageView) {
        return segmentAnalytics.trackPageView(payload as PageViewEvent);
      } else if (payload.evtName === EventType.ModalView) {
        return segmentAnalytics.trackModalView(payload as ModalViewEvent);
      } else {
        console.warn(`Unknown event name: ${payload.evtName}`);
        return Promise.resolve();
      }
    };
  }

  return <>{children(outerContextValue.current)}</>;
};
